import { EliteLandingPageState } from '../../types/misc.types'
import { EventNames } from '../../types/misc.types'
import { postCTA, trackEvent } from '../../utils/appBridge'
import Button from '../ui-components/Button'
import { formatNumberWithoutCurrency } from '../../utils/utils'
import { eliteLandingPageStateConfig } from '../../utils/eliteLandingPageConfig'
import '../../components/skeletons/skeleton.css'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import UpgradeAppContent from '../BottomSheetComponents/UpgradeAppContent'
import RenderHTML from '../ui-components/RenderHTML'
import { useState, useEffect, useMemo, useRef } from 'react'
import { getSessionStorageItem, setSessionStorageItem } from '../../utils/sessionStorage'
import { EliteConsentTextContent, hasEliteConsentText } from './EliteConsentTextContent'
import type { EliteConsentTextItem, EliteReferralReward } from '../../types/misc.types'
import { GROUPED_STATIC_TEXT_ROLE } from '../ui-components/NameValueTableRow'
import {
  getEliteReferralRewardCopy,
  hasEliteReferralReward
} from './EliteReferralRewardBanner'
import { useEliteBuyCtaExtras } from './EliteBuyCtaExtrasContext'

type EliteButtonPropTypes = {
  eliteLandingPageState: `${EliteLandingPageState}`
  amount: number
  cta: Record<any, any> | null
  isForceUpdateRequired: boolean
  version: 'V1' | 'V2' | 'V3' | 'V4' | 'Rebalancing_Timer' | 'ELITE_LANDING_PAGE_1L'
  isButtonDisabled?: boolean
  theme?: 'ELITE' | 'INVERTED_ELITE'
  consentText?: string
  consentTextList?: EliteConsentTextItem[]
  isEliteButtonVisible: boolean
  familyMemberName?: string
  onConsentStateChange?: (checked: boolean) => void
  specialOffering?: boolean
  showConsentText?: boolean
  referralReward?: EliteReferralReward | null
}

export interface ConsentState {
  checked: boolean
  expanded: boolean
}

const CONSENT_STATE_KEY = 'consentState'

const defaultConsentState: ConsentState = {
  checked: true,
  expanded: false
}

function parseConsentState(stored: string | null): ConsentState {
  if (!stored) return defaultConsentState
  try {
    const parsed = JSON.parse(stored) as Partial<ConsentState> & {
      topChecked?: boolean
      bottomChecked?: boolean
      topExpanded?: boolean
      bottomExpanded?: boolean
    }
    if ('checked' in parsed) {
      return {
        checked: parsed.checked ?? false,
        expanded: parsed.expanded ?? false
      }
    }
    if ('topChecked' in parsed || 'bottomChecked' in parsed) {
      return {
        checked: Boolean(parsed.topChecked && parsed.bottomChecked),
        expanded: Boolean(parsed.topExpanded || parsed.bottomExpanded)
      }
    }
    return defaultConsentState
  } catch {
    return defaultConsentState
  }
}

export const EliteButtonV3 = ({
  eliteLandingPageState,
  amount,
  theme = 'ELITE',
  cta,
  isForceUpdateRequired,
  version,
  isButtonDisabled,
  consentText: _consentText,
  consentTextList,
  isEliteButtonVisible,
  familyMemberName,
  onConsentStateChange,
  specialOffering,
  showConsentText = true,
  referralReward,
}: EliteButtonPropTypes) => {
  const { topLayerBg, topLayerTextColor, buttonBg, buttonTextColor } = eliteButtonConfig?.[theme]
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  // Screen-level buy-CTA extras (plan ids + price). Local `amount` is the price this
  // button renders; keep it as a guaranteed floor so the Upgrade tap always has a
  // value even before the advisory query resolves. A real screen amount overrides it.
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const buyCtaExtras = { ...eliteBuyCtaExtras, amount: eliteBuyCtaExtras.amount ?? String(amount) }
  const [consentState, setConsentState] = useState<ConsentState>(defaultConsentState)
  const priceMessageRef = useRef<HTMLDivElement>(null)
  const [dynamicHeight, setDynamicHeight] = useState<number>(80)

  const isEliteNotAllowedForFamily = useMemo(
    () => eliteLandingPageState === EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED,
    [eliteLandingPageState]
  )

  const hasConsent = hasEliteConsentText(consentTextList)

  useEffect(() => {
    if (!hasConsent) {
      const autoCheckedState: ConsentState = { checked: true, expanded: false }
      setConsentState(autoCheckedState)
      setSessionStorageItem(CONSENT_STATE_KEY, JSON.stringify(autoCheckedState))
      onConsentStateChange?.(true)
      return
    }

    const storedState = parseConsentState(getSessionStorageItem(CONSENT_STATE_KEY))
    setConsentState(storedState)
    onConsentStateChange?.(storedState.checked)
  }, [isEliteButtonVisible, hasConsent, onConsentStateChange])

  const hasReferralReward = hasEliteReferralReward(referralReward)
  const referralRewardCopy = hasReferralReward ? getEliteReferralRewardCopy(referralReward!) : undefined
  const showTopStrip =
    hasReferralReward ||
    (!specialOffering &&
      Boolean(eliteLandingPageStateConfig?.[eliteLandingPageState]?.topText) &&
      !(hasConsent && theme === 'INVERTED_ELITE'))

  useEffect(() => {
    if (hasReferralReward) {
      setDynamicHeight(90)
      return
    }
    if (isEliteNotAllowedForFamily && priceMessageRef?.current) {
      requestAnimationFrame(() => {
        if (priceMessageRef.current) {
          const contentHeight = priceMessageRef.current.scrollHeight
          const totalHeight = contentHeight + 16
          setDynamicHeight(Math.max(totalHeight, 80))
        }
      })
    } else {
      setDynamicHeight(80)
    }
  }, [isEliteNotAllowedForFamily, hasReferralReward, eliteLandingPageState, familyMemberName])

  const getEliteButtonTextColor = () => {
    if (isButtonDisabled) return '!text-[#88898C]'
    if (hasReferralReward && !consentState.checked) return '!text-[#C4896A]'
    if (hasReferralReward) return '!text-[#992E00]'
    return buttonTextColor
  }

  const getEliteButtonBg = () => {
    if (isButtonDisabled) return '!bg-[#4B4C4E]'
    if (hasReferralReward && !consentState.checked) return '!bg-[#FFF2DF]'
    if (hasReferralReward) return '!bg-[#FFF2DF]'
    return buttonBg
  }

  const getConsentNotCheckedClass = () => {
    if (consentState.checked) return ''
    if (hasReferralReward) return ''
    return eliteButtonConfig?.[theme]?.consentNotCheckedClassName
  }

  const renderPriceMessage = () => {
    if (hasReferralReward && referralRewardCopy) {
      return (
        <div className='mx-auto mt-[1px] text-center'>
          <div className='text-[13px] font-medium leading-[1.3] text-white'>{referralRewardCopy.title}</div>
          {referralRewardCopy.subtitle ? (
            <div className='mt-0.5 text-[12px] leading-[1.3] text-[#FFE8D0]'>{referralRewardCopy.subtitle}</div>
          ) : null}
        </div>
      )
    }
    if (isEliteNotAllowedForFamily)
      return (
        <>
          <div className='text-[13px] mx-auto mt-[1px] pb-[10px] text-center'>
            {eliteLandingPageStateConfig?.[eliteLandingPageState]?.topText?.replace(
              '{familyMemberName}',
              familyMemberName?.split(' ')?.[0] ?? 'your family member'
            )}
          </div>
        </>
      )
    return amount ? (
      <div
        role={GROUPED_STATIC_TEXT_ROLE}
        aria-label='Special price for next 10k members'
        className='text-[13px] mx-auto mt-[1px]'
      >
        <span aria-hidden='true'>Special price for next 10k members</span>
      </div>
    ) : (
      <>
        <div className='text-[13px] pt-[2px]'>Unable to load</div>
      </>
    )
  }

  const renderConsentText = (checkedIcon: string, unCheckedIcon: string) => {
    if (!hasConsent) return null

    return (
      <div
        className={`relative z-30 flex w-full gap-2 ${theme === 'INVERTED_ELITE' ? 'pt-3' : 'pt-5'} ${showConsentText ? 'pointer-events-auto' : 'invisible pointer-events-none'
          }`}
        aria-hidden={!showConsentText}
      >
        <div
          className='cursor-pointer shrink-0'
          onClick={() => {
            setConsentState((prev) => {
              const newState = { ...prev, checked: !prev.checked }
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ...buyCtaExtras,
                  eventId: version,
                  buttonLabel: newState.checked ? 'Consent Checked' : 'Consent UnChecked',
                  screenType: version === 'ELITE_LANDING_PAGE_1L' ? version : 'MF_Elite_Landing'
                }
              })
              setSessionStorageItem(CONSENT_STATE_KEY, JSON.stringify(newState))
              onConsentStateChange?.(newState.checked)
              return newState
            })
          }}
        >
          <RenderHTML innerHTMLText={consentState.checked ? checkedIcon : unCheckedIcon} />
        </div>
        <div className='relative z-20 flex-1 max-w-[90%] text-[12px] text-[#8E9393] -mt-[1px]'>
          <EliteConsentTextContent
            consentTextList={consentTextList}
            readMoreExpanded={consentState.expanded}
            onReadMoreExpand={(e) => {
              e.stopPropagation()
              setConsentState((prev) => {
                const newState = { ...prev, expanded: true }
                setSessionStorageItem(CONSENT_STATE_KEY, JSON.stringify(newState))
                return newState
              })
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col w-full`}>
      <div className={hasReferralReward ? 'overflow-hidden rounded-[12px]' : ''}>
        {showTopStrip ? (
          <div
            className={`relative z-0 ${hasReferralReward ? 'rounded-none bg-[#CC5C2D]' : `rounded-[12px] ${topLayerBg}`}`}
            style={{ height: `${dynamicHeight}px` }}
          >
            <div className='h-[75px]'></div>
            <div
              ref={priceMessageRef}
              className={`absolute flex w-full ${!amount && !hasReferralReward ? 'justify-center' : 'justify-between'} items-center pb-8 px-[14px] ${hasReferralReward ? 'text-white' : topLayerTextColor} z-10 top-[5px]`}
            >
              {renderPriceMessage()}
            </div>
          </div>
        ) : (
          <div className='h-8 w-full'></div>
        )}

        <Button
          onClick={() => {
            if (hasConsent && !consentState.checked) return
            else if (isForceUpdateRequired) openBottomSheet(<UpgradeAppContent onDismiss={closeBottomSheet} />)
            else if (cta && !isButtonDisabled) {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ...buyCtaExtras,
                  eventId: version,
                  buttonLabel: 'Get Notified',
                  screenType: version === 'ELITE_LANDING_PAGE_1L' ? version : 'MF_Elite_Landing'
                }
              })
              postCTA(JSON.stringify(cta))
            }
          }}
          aria-label={
            eliteLandingPageState === EliteLandingPageState.RE_INITIATE_PAYMENT
              ? 'Retry payment'
              : undefined
          }
          aria-description={
            eliteLandingPageState === EliteLandingPageState.RE_INITIATE_PAYMENT
              ? 'Double tap to retry the payment'
              : undefined
          }
          buttonClassName={`min-h-12 min-w-full mt-[-44px] font-[500] relative z-10 text-[14px] rounded-[12px] 
          ${getEliteButtonBg()} ${getConsentNotCheckedClass()}`}
        >
          <div className='flex gap-2 items-center justify-center'>
            <div className={`pb-[2px] ${getEliteButtonTextColor()}`}>
              <RenderHTML
                innerHTMLText={
                  eliteLandingPageStateConfig?.[eliteLandingPageState]?.buttonText?.replace(
                    '{amount}',
                    `${formatNumberWithoutCurrency(amount)}`
                  ) ?? ''
                }
              />
            </div>
          </div>
        </Button>
      </div>

      {renderConsentText(eliteButtonConfig?.[theme]?.checkedIcon, eliteButtonConfig?.[theme]?.unCheckedIcon)}
    </div>
  )
}

const eliteButtonConfig: Record<
  string,
  {
    topLayerBg: string
    topLayerTextColor: string
    buttonBg: string
    buttonTextColor: string
    consentNotCheckedClassName: string
    unCheckedIcon: string
    checkedIcon: string
  }
> = {
  ['ELITE']: {
    topLayerBg: 'bg-gradient-to-br from-[#2B3238] to-[#171B20]',
    topLayerTextColor: 'text-[#FEFFFE]',
    buttonBg: '!bg-[#FFF2DF]',
    buttonTextColor: '!text-[#992E00]',
    consentNotCheckedClassName: 'opacity-50',
    unCheckedIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect x="1" y="1" width="18" height="18" rx="5" stroke="#8E9393" stroke-width="2"/>
</svg>`,
    checkedIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect width="20" height="20" rx="6" fill="#3D3D3D"/>
  <path d="M5.41504 10.4199L7.9958 12.6781C8.41143 13.0418 9.0432 12.9996 9.40688 12.584L14.5817 6.66992" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
  },
  ['INVERTED_ELITE']: {
    topLayerBg: 'bg-[#FFF2DF]',
    topLayerTextColor: 'text-[#992E00]',
    buttonBg: '!bg-[#232323]',
    buttonTextColor: '!text-[#FEFFFE]',
    consentNotCheckedClassName: '!bg-[#D1D6D6]',
    unCheckedIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect x="1" y="1" width="18" height="18" rx="5" stroke="#8E9393" stroke-width="2"/>
</svg>`,
    checkedIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect width="20" height="20" rx="6" fill="#3D3D3D"/>
  <path d="M5.41504 10.4199L7.9958 12.6781C8.41143 13.0418 9.0432 12.9996 9.40688 12.584L14.5817 6.66992" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
  }
}
